import { useEffect, useLayoutEffect, useState } from "react";
import ProductService from "../service/ProductService";
import SearchService from "../service/SearchService";
import { parseFringeBenefit, parseProduct } from "../utilities/parsers/product";
import { useHistory, useLocation } from "react-router-dom";
import localeText from "../locale";
import { locale } from "../utilities/locale/locale";

const useSearchProducts = ({ searchTerm, onSearchError } = {}) => {
  const productService = new ProductService();
  const searchService = new SearchService();

  const history = useHistory();
  const url = useLocation();
  const params = new URLSearchParams(url?.search);
  const isFringeBenefitsPage = url?.pathname?.toLowerCase()?.includes("fringe");

  const [loadingProducts, setLoadingProducts] = useState(false);
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchingInAll, setSearchingInAll] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const [fringeBenefitsSubCategories, setFringeBenefitsSubCategories] = useState([]);

  const [searchParams, setSearchParams] = useState({
    currency: params?.get("currency") ? params?.get("currency") : "ALL",
    type: params?.get("type") ? params?.get("type") : "ALL",
    seller: params?.get("seller") ? params?.get("seller") : "ALL",
    area: params?.get("area") ? params?.get("area") : "ALL",
    category: params?.get("category") ? params?.get("category") : "ALL",
  });

  const [advancedSearchParams, setAdvancedSearchParams] = useState({
    currency: params?.get("currency") ? params?.get("currency") : "ALL",
    type: params?.get("type") ? params?.get("type") : "ALL",
    seller: params?.get("seller") ? params?.get("seller") : "ALL",
    area: params?.get("area") ? params?.get("area") : "ALL",
  });

  const fetchProducts = async () => {
    /* console.log(searchTerm.seller); */
    if (!searchTerm) return;

    try {
      setLoadingProducts(true);

      let resSearch;

      if (isFringeBenefitsPage) {
        resSearch = await productService.searchFringeBenefit({ expression: searchTerm });
      } else {
        resSearch = await productService.searchByTitle(
          searchTerm,
          searchParams.currency,
          searchParams.type,
          searchParams.seller,
          searchParams.area,
          searchParams.category,
        );
      }

      if (resSearch.messageCode !== 200001) {
        onSearchError(resSearch.messageCode);
        setLoadingProducts(false);
      } else {
        setSearchMessage(`${searchTerm}`);
        setSearchingInAll(true);

        if (isFringeBenefitsPage) {
          let productsArr = resSearch.result.content
            .filter((product) => product.productStatus === "PUBLISHED")
            .map(parseFringeBenefit);

          if (searchParams.currency && searchParams.currency !== "ALL") {
            productsArr = productsArr.filter(
              (product) => product.currency === searchParams.currency,
            );
          }

          if (searchParams.currency && searchParams.area !== "ALL") {
            productsArr = productsArr.filter(
              (product) => product.user?.country === searchParams.area,
            );
          }

          if (searchTerm && searchTerm !== "ALL") {
            productsArr = productsArr.filter((product) =>
              product.title.toLowerCase().includes(searchTerm.toLowerCase()),
            );
          }

          const subCategoriesParsed = productsArr.map((product) => {
            const subCategory = product.subCategory;

            const translations = {
              en: subCategory?.descriptionEnglish,
              it: subCategory?.descriptionItalian,
              pt: subCategory?.descriptionPortuguese,
              es: subCategory?.descriptionSpanish,
            };

            return {
              name: translations[locale],
              code: subCategory.id,
            };
          });

          const allSubCategories = [
            {
              name: localeText("all"),
              code: "ALL",
            },
            ...subCategoriesParsed,
          ];

          setFringeBenefitsSubCategories(allSubCategories);
          setProducts(productsArr);
          setLoadingProducts(false);
          setTotalProducts(resSearch.result.totalElements);
          setTotalPages(resSearch.result.totalPages);

          return;
        }

        setProducts(resSearch.result.content);
        setTotalProducts(resSearch.result.totalElements);
        setTotalPages(resSearch.result.totalPages);
        setLoadingProducts(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const search = async ({ expression, category, onError }) => {
    try {
      if (expression.length > 0) {
        setLoadingSearch(true);

        const usernameSession = localStorage.getItem("__extraconomy_username");

        const res = await searchService.postRegisterSearch(
          expression,
          usernameSession ? usernameSession : "",
        );

        if (res.messageCode !== 200000) {
          onError(res.messageCode);
          if (res.messageCode === 500000) console.log(res);
          setLoadingSearch(false);
        } else {
          localStorage.setItem("__extraconomy_search", JSON.stringify(res.result));
          setLoadingSearch(false);

          if (expression === "ALL") {
            if (isFringeBenefitsPage && category.id === -1) {
              history.push(
                `/fringe-benefits?search=${expression}&currency=${advancedSearchParams.currency}&area=${advancedSearchParams.area}`,
              );
              return;
            }

            history.push(
              `/shop?search=${expression}&currency=${
                advancedSearchParams.currency
              }&type=${advancedSearchParams.type}&seller=${advancedSearchParams.seller}&area=${
                advancedSearchParams.area
              }&category=${category ? category.id : "ALL"}&page=0&size=12`,
            );
          } else {
            if (isFringeBenefitsPage && category.id === -1) {
              history.push(`/fringe-benefits?search=${expression}&currency=ALL&area=ALL`);
              return;
            }

            history.push(
              `/shop?search=${expression}&currency=ALL&type=ALL&seller=ALL&area=ALL&category=${
                category ? category.id : "ALL"
              }&page=0&size=12`,
            );
          }
        }
      }
    } catch (e) {
      if (e.status === 401 || e.status === 403) {
        localStorage.removeItem("__extraconomy_token");
        localStorage.removeItem("__extraconomy_username");
        window.location.replace("/login");
      }
      setLoadingSearch(false);
      console.log(e);
    }
  };

  useLayoutEffect(() => {
    setLoadingProducts(true);
    setSearchParams({
      currency: params?.get("currency") ? params?.get("currency") : "ALL",
      type: params?.get("type") ? params?.get("type") : "ALL",
      seller: params?.get("seller") ? params?.get("seller") : "ALL",
      area: params?.get("area") ? params?.get("area") : "ALL",
      category: params?.get("category") ? params?.get("category") : "ALL",
    });
    setAdvancedSearchParams({
      currency: params?.get("currency") ? params?.get("currency") : "ALL",
      type: params?.get("type") ? params?.get("type") : "ALL",
      seller: params?.get("seller") ? params?.get("seller") : "ALL",
      area: params?.get("area") ? params?.get("area") : "ALL",
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url.search]);

  useEffect(() => {
    fetchProducts();
    return () => {
      setLoadingProducts(false);
      setProducts([]);
      setTotalPages(0);
      setTotalProducts(0);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return {
    loadingProducts,
    products,
    totalProducts,
    totalPages,
    searchingInAll,
    loadingSearch,
    searchMessage,
    searchParams,
    advancedSearchParams,
    fringeBenefitsSubCategories,
    setAdvancedSearchParams,
    setSearchParams,
    search,
    setProducts,
    setLoadingProducts,
    setSearchMessage,
  };
};

export default useSearchProducts;
